import React, { useState, useCallback, useEffect } from 'react';
import api from '../../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/TransferenciaNotas.css';

function TransferenciaNotas() {
  // Estados principais
  const [searchTerm, setSearchTerm] = useState('');
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [alunosBusca, setAlunosBusca] = useState([]);
  const [notas, setNotas] = useState([]);
  const [notasSelecionadas, setNotasSelecionadas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [cursoDestino, setCursoDestino] = useState('');
  const [observacao, setObservacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [loadingNotas, setLoadingNotas] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [matriculas, setMatriculas] = useState([]);
  const [filtroSituacao, setFiltroSituacao] = useState('todas');

  // Buscar cursos disponíveis ao montar o componente
  useEffect(() => {
    carregarCursos();
  }, []);

  const carregarCursos = async () => {
    try {
      const response = await api.get('/transferencia-notas/cursos');
      setCursos(response.data);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      toast.error('Erro ao carregar lista de cursos');
    }
  };

  // Buscar alunos pelo termo de busca
  const buscarAlunos = useCallback(async (termo) => {
    if (!termo || termo.length < 3) {
      setAlunosBusca([]);
      setShowDropdown(false);
      return;
    }

    try {
      setLoadingBusca(true);
      const response = await api.get(`/transferencia-notas/alunos/buscar?search=${encodeURIComponent(termo)}`);
      setAlunosBusca(response.data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Erro ao buscar alunos:', error);
      toast.error('Erro ao buscar alunos');
    } finally {
      setLoadingBusca(false);
    }
  }, []);

  // Debounce para busca de alunos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        buscarAlunos(searchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, buscarAlunos]);

  // Selecionar um aluno da busca
  const selecionarAluno = async (aluno) => {
    setAlunoSelecionado(aluno);
    setSearchTerm(aluno.nome);
    setShowDropdown(false);
    setNotasSelecionadas([]);
    setCursoDestino('');
    
    // Carregar notas do aluno
    await carregarNotasAluno(aluno.idPessoa);
  };

  // Carregar notas de um aluno
  const carregarNotasAluno = async (idAluno) => {
    try {
      setLoadingNotas(true);
      const response = await api.get(`/transferencia-notas/alunos/${idAluno}/notas`);
      
      setNotas(response.data.notas.todas);
      setResumo(response.data.resumo);
      setMatriculas(response.data.matriculas);
      
      // Se tiver notas órfãs, já selecionar automaticamente o filtro para órfãs
      if (response.data.resumo.notasOrfas > 0) {
        setFiltroSituacao('orfas');
      } else {
        setFiltroSituacao('todas');
      }
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
      toast.error('Erro ao carregar notas do aluno');
    } finally {
      setLoadingNotas(false);
    }
  };

  // Filtrar notas pela situação
  const notasFiltradas = notas.filter(nota => {
    if (filtroSituacao === 'todas') return true;
    if (filtroSituacao === 'orfas') return nota.situacao === 'orfa';
    if (filtroSituacao === 'vinculadas') return nota.situacao === 'vinculada';
    return true;
  });

  // Selecionar/deselecionar uma nota
  const toggleNotaSelecionada = (notaId) => {
    setNotasSelecionadas(prev => {
      if (prev.includes(notaId)) {
        return prev.filter(id => id !== notaId);
      } else {
        return [...prev, notaId];
      }
    });
  };

  // Selecionar todas as notas filtradas
  const selecionarTodas = () => {
    const todasIds = notasFiltradas.map(n => n.id);
    setNotasSelecionadas(todasIds);
  };

  // Deselecionar todas
  const deselecionarTodas = () => {
    setNotasSelecionadas([]);
  };

  // Transferir notas selecionadas
  const transferirNotas = async () => {
    if (!alunoSelecionado) {
      toast.warning('Selecione um aluno');
      return;
    }

    if (notasSelecionadas.length === 0) {
      toast.warning('Selecione pelo menos uma nota para transferir');
      return;
    }

    if (!cursoDestino) {
      toast.warning('Selecione o curso de destino');
      return;
    }

    // Verificar se alguma nota selecionada já está no curso de destino
    const notasNoDestinoMesmo = notas.filter(
      n => notasSelecionadas.includes(n.id) && n.cursoId === parseInt(cursoDestino)
    );

    if (notasNoDestinoMesmo.length > 0) {
      toast.warning(`${notasNoDestinoMesmo.length} nota(s) já está(ão) no curso de destino selecionado`);
      return;
    }

    const cursoDestinoNome = cursos.find(c => c.idCurso === parseInt(cursoDestino))?.nome || '';

    if (!window.confirm(`Confirma a transferência de ${notasSelecionadas.length} nota(s) para o curso "${cursoDestinoNome}"?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/transferencia-notas/transferir', {
        idAluno: alunoSelecionado.idPessoa,
        notasIds: notasSelecionadas,
        cursoDestino: parseInt(cursoDestino),
        observacao
      });

      toast.success(response.data.message);
      
      // Recarregar notas do aluno
      await carregarNotasAluno(alunoSelecionado.idPessoa);
      
      // Limpar seleções
      setNotasSelecionadas([]);
      setCursoDestino('');
      setObservacao('');
    } catch (error) {
      console.error('Erro ao transferir notas:', error);
      toast.error(error.response?.data?.error || 'Erro ao transferir notas');
    } finally {
      setLoading(false);
    }
  };

  // Limpar busca e começar nova
  const limparBusca = () => {
    setSearchTerm('');
    setAlunoSelecionado(null);
    setNotas([]);
    setNotasSelecionadas([]);
    setResumo(null);
    setMatriculas([]);
    setCursoDestino('');
    setObservacao('');
    setFiltroSituacao('todas');
  };

  // Formatar data
  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  return (
    <div className="transferencia-notas-container">
      <ToastContainer 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <div className="page-header">
        <h1>Transferência de Notas entre Cursos</h1>
        <p className="subtitle">
          Transfira notas de um curso para outro quando houver lançamento em curso incorreto
        </p>
      </div>

      <div className="content-wrapper">
        {/* Card de busca do aluno */}
        <div className="card busca-card">
          <h2>1. Buscar Aluno</h2>
          
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Digite o nome do aluno (mínimo 3 caracteres)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                disabled={loadingBusca}
              />
              {loadingBusca && <span className="loading-spinner">⏳</span>}
              {alunoSelecionado && (
                <button 
                  className="btn-limpar"
                  onClick={limparBusca}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Dropdown de resultados */}
            {showDropdown && alunosBusca.length > 0 && !alunoSelecionado && (
              <ul className="dropdown-list">
                {alunosBusca.map(aluno => (
                  <li 
                    key={aluno.idPessoa}
                    onClick={() => selecionarAluno(aluno)}
                    className="dropdown-item"
                  >
                    <strong>{aluno.nome}</strong>
                    <span className="info-secundaria">
                      {aluno.cpf && `CPF: ${aluno.cpf}`}
                      {aluno.extensaoNome && ` | Extensão: ${aluno.extensaoNome}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            
            {showDropdown && alunosBusca.length === 0 && searchTerm.length >= 3 && !loadingBusca && (
              <div className="dropdown-empty">
                Nenhum aluno encontrado
              </div>
            )}
          </div>

          {/* Info do aluno selecionado */}
          {alunoSelecionado && (
            <div className="aluno-info">
              <div className="info-grid">
                <div className="info-item">
                  <label>Nome:</label>
                  <span>{alunoSelecionado.nome}</span>
                </div>
                <div className="info-item">
                  <label>CPF:</label>
                  <span>{alunoSelecionado.cpf || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Extensão:</label>
                  <span>{alunoSelecionado.extensaoNome || '-'}</span>
                </div>
              </div>
              
              {/* Matrículas ativas */}
              {matriculas.length > 0 && (
                <div className="matriculas-box">
                  <h4>Matrículas Ativas:</h4>
                  <ul>
                    {matriculas.map(m => (
                      <li key={m.idMatricula}>
                        <strong>{m.cursoNome}</strong> - Matrícula: {m.numeroMatricula} ({m.status})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Card de notas */}
        {alunoSelecionado && (
          <div className="card notas-card">
            <div className="card-header-flex">
              <h2>2. Notas do Aluno</h2>
              
              {resumo && (
                <div className="resumo-notas">
                  <span className="badge total">Total: {resumo.totalNotas}</span>
                  <span className="badge vinculadas">Vinculadas: {resumo.notasVinculadas}</span>
                  <span className={`badge orfas ${resumo.notasOrfas > 0 ? 'destaque' : ''}`}>
                    Órfãs: {resumo.notasOrfas}
                  </span>
                </div>
              )}
            </div>
            
            {/* Filtros */}
            <div className="filtros-container">
              <label>Filtrar por situação:</label>
              <div className="filtros-btns">
                <button 
                  className={`filtro-btn ${filtroSituacao === 'todas' ? 'active' : ''}`}
                  onClick={() => setFiltroSituacao('todas')}
                >
                  Todas
                </button>
                <button 
                  className={`filtro-btn ${filtroSituacao === 'orfas' ? 'active' : ''}`}
                  onClick={() => setFiltroSituacao('orfas')}
                >
                  🔴 Órfãs (sem matrícula)
                </button>
                <button 
                  className={`filtro-btn ${filtroSituacao === 'vinculadas' ? 'active' : ''}`}
                  onClick={() => setFiltroSituacao('vinculadas')}
                >
                  🟢 Vinculadas
                </button>
              </div>
            </div>

            {loadingNotas ? (
              <div className="loading-notas">Carregando notas...</div>
            ) : notasFiltradas.length === 0 ? (
              <div className="empty-notas">
                {filtroSituacao === 'orfas' 
                  ? 'Nenhuma nota órfã encontrada' 
                  : 'Nenhuma nota encontrada'}
              </div>
            ) : (
              <>
                {/* Ações em lote */}
                <div className="acoes-lote">
                  <button 
                    className="btn-secondary btn-sm"
                    onClick={selecionarTodas}
                  >
                    Selecionar todas ({notasFiltradas.length})
                  </button>
                  <button 
                    className="btn-secondary btn-sm"
                    onClick={deselecionarTodas}
                    disabled={notasSelecionadas.length === 0}
                  >
                    Limpar seleção
                  </button>
                  <span className="selecionadas-count">
                    {notasSelecionadas.length} nota(s) selecionada(s)
                  </span>
                </div>
                
                {/* Tabela de notas */}
                <div className="tabela-container">
                  <table className="notas-table">
                    <thead>
                      <tr>
                        <th className="col-check"></th>
                        <th>Disciplina</th>
                        <th>Curso Atual</th>
                        <th className="col-center">Nota</th>
                        <th className="col-center">CH</th>
                        <th className="col-center">Data</th>
                        <th className="col-center">Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notasFiltradas.map(nota => (
                        <tr 
                          key={nota.id}
                          className={`
                            ${nota.situacao === 'orfa' ? 'row-orfa' : ''} 
                            ${notasSelecionadas.includes(nota.id) ? 'row-selecionada' : ''}
                          `}
                        >
                          <td className="col-check">
                            <input 
                              type="checkbox"
                              checked={notasSelecionadas.includes(nota.id)}
                              onChange={() => toggleNotaSelecionada(nota.id)}
                            />
                          </td>
                          <td>{nota.disciplinaNome}</td>
                          <td>{nota.cursoNome || <em className="curso-nulo">Não definido</em>}</td>
                          <td className="col-center">
                            <span className="nota-valor">{nota.media != null ? parseFloat(nota.media).toFixed(1) : '-'}</span>
                          </td>
                          <td className="col-center">{nota.cargaHoraria || '-'}</td>
                          <td className="col-center">{formatarData(nota.dataRealizacao)}</td>
                          <td className="col-center">
                            {nota.situacao === 'orfa' ? (
                              <span className="badge-situacao orfa" title="Nota sem matrícula correspondente no curso">
                                🔴 Órfã
                              </span>
                            ) : (
                              <span className="badge-situacao vinculada">
                                🟢 OK
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Card de transferência */}
        {alunoSelecionado && notasSelecionadas.length > 0 && (
          <div className="card transferencia-card">
            <h2>3. Transferir para Curso</h2>
            
            <div className="form-group">
              <label>Curso de Destino <span className="required">*</span></label>
              <select
                value={cursoDestino}
                onChange={(e) => setCursoDestino(e.target.value)}
                className="form-control"
              >
                <option value="">Selecione o curso de destino...</option>
                {cursos.map(curso => (
                  <option key={curso.idCurso} value={curso.idCurso}>
                    {curso.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Observação (opcional)</label>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                className="form-control"
                rows="3"
                placeholder="Motivo da transferência..."
              />
            </div>

            <div className="resumo-transferencia">
              <p>
                <strong>{notasSelecionadas.length}</strong> nota(s) será(ão) transferida(s)
              </p>
            </div>

            <button
              className="btn-primary btn-transferir"
              onClick={transferirNotas}
              disabled={loading || !cursoDestino}
            >
              {loading ? 'Transferindo...' : '✓ Confirmar Transferência'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransferenciaNotas;
